import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { FeatureFlagContext } from '@lotta/self-service/lottery-fe/util-feature-flags';
import { useContext } from 'react';
import { isEmailMarketingConstraint } from '@lotta/util-lottery-type-guards';
import InlineMessage from '@ingka/inline-message';
import { Trans, useTranslation } from 'react-i18next';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { useFormModule } from '../../utils/use-form-module';

export const MODULE_NAME = 'email-marketing-constraint';

export type EmailMarketingConstraintFormType = Static<typeof EmailMarketingConstraintFormSchema>;
export const EmailMarketingConstraintFormSchema = Type.Object(
  {
    emailMarketingRequired: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false, $id: 'EmailMarketingConstraintFormSchema' }
);

const defaultValuesNotRequired: EmailMarketingConstraintFormType = {};
const defaultValuesRequired: EmailMarketingConstraintFormType = { emailMarketingRequired: true };

export const MaxWinnersPerStoreFormTransformer: ModuleTransformer<EmailMarketingConstraintFormType> =
  {
    fromPayload: ({ lottery }) => {
      const constraint = lottery.drawConstraints?.find(isEmailMarketingConstraint);

      if (!constraint) {
        return {};
      }

      return {
        emailMarketingRequired: constraint.emailMarketingRequired,
      };
    },
    toPayload: (formData) => {
      if (formData.emailMarketingRequired === undefined) {
        return {};
      }

      return {
        lottery: {
          drawConstraints: [
            {},
            {
              emailMarketingRequired: formData.emailMarketingRequired,
            },
          ],
        },
      };
    },
  };

export type EmailMarketingConstraintFormProps = FormModuleProps<EmailMarketingConstraintFormType>;

export const EmailMarketingConstraintForm: React.FC<EmailMarketingConstraintFormProps> = ({
  values,
  onChange,
  onValidation,
}) => {
  const featureFlags = useContext(FeatureFlagContext);
  const { t } = useTranslation();

  const defaultValues = featureFlags.requireEmailMarketingConstraint
    ? defaultValuesRequired
    : defaultValuesNotRequired;

  useFormModule({
    moduleName: MODULE_NAME,
    defaultValues: values || defaultValues,
    schema: EmailMarketingConstraintFormSchema,
    onChange,
    onValidation,
  });

  if (!featureFlags.requireEmailMarketingConstraint) {
    return null;
  }

  return (
    <InlineMessage
      variant="informative"
      title={`${t('form-explanation-message-email-marketing-constraint-title')}`}
      body={<Trans i18nKey="form-explanation-message-email-marketing-constraint-body" />}
    />
  );
};

export const module = {
  name: MODULE_NAME,
  transformer: MaxWinnersPerStoreFormTransformer,
  form: EmailMarketingConstraintForm,
} as const;
